import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { vcFundsApi } from '../lib/api';
import { cn } from '../lib/utils';
import {
  Building2, Loader2, DollarSign, Target, Users,
  ChevronRight, Globe, MapPin, TrendingUp, Filter, ExternalLink
} from 'lucide-react';

const formatCheckSize = (sizes) => {
  if (!sizes) return 'N/A';
  const entries = Object.entries(sizes);
  if (entries.length === 0) return 'N/A';
  return entries.map(([stage, range]) =>
    `${stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: $${range}`
  ).join(' | ');
};

const VCFundsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [funds, setFunds] = useState([]);
  const [stageFilter, setStageFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [expandedFund, setExpandedFund] = useState(null);
  const [fundDetail, setFundDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const stages = ['seed', 'early', 'growth', 'late'];
  const sectors = ['AI/ML', 'Fintech', 'SaaS', 'Healthcare', 'Infrastructure', 'Consumer', 'Enterprise', 'Crypto/Web3', 'Climate', 'Developer Tools'];

  const fetchFunds = async () => {
    setLoading(true);
    try {
      const response = await vcFundsApi.list({
        stage: stageFilter || undefined,
        sector: sectorFilter || undefined,
      });
      setFunds(response.data.funds || []);
    } catch (err) {
      console.error('Failed to fetch VC funds:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFunds(); }, [stageFilter, sectorFilter]);

  const toggleExpand = async (fundName) => {
    if (expandedFund === fundName) {
      setExpandedFund(null);
      setFundDetail(null);
      return;
    }
    setExpandedFund(fundName);
    setLoadingDetail(true);
    try {
      const response = await vcFundsApi.get(fundName);
      setFundDetail(response.data);
    } catch (err) {
      console.error('Failed to fetch fund detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-light font-['Outfit'] text-zinc-50">VC Funds</h1>
          <p className="text-zinc-400 font-['Manrope']">
            <span className="text-zinc-50 font-medium">{funds.length}</span> funds profiled with investment thesis, entry stage, and check sizes
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <Select value={stageFilter} onValueChange={(v) => setStageFilter(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="vc-filter-stage" className="w-40 bg-zinc-900 border-zinc-800 text-zinc-50">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all" className="text-zinc-50">All Stages</SelectItem>
              {stages.map((s) => (
                <SelectItem key={s} value={s} className="text-zinc-50 hover:bg-zinc-700 capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sectorFilter} onValueChange={(v) => setSectorFilter(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="vc-filter-sector" className="w-44 bg-zinc-900 border-zinc-800 text-zinc-50">
              <Target className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Sectors" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700 max-h-80">
              <SelectItem value="all" className="text-zinc-50">All Sectors</SelectItem>
              {sectors.map((s) => (
                <SelectItem key={s} value={s} className="text-zinc-50 hover:bg-zinc-700">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Fund Cards */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {funds.map((fund) => (
              <div key={fund.name} data-testid={`vc-fund-${fund.name}`}>
                <div
                  onClick={() => toggleExpand(fund.name)}
                  className={cn(
                    "bg-zinc-900 border rounded-md p-5 cursor-pointer transition-all duration-200",
                    expandedFund === fund.name ? "border-zinc-600" : "border-zinc-800 hover:border-zinc-700"
                  )}
                >
                  <div className="flex items-start gap-4">
                    {/* Logo */}
                    <div className="w-14 h-14 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-7 h-7 text-zinc-500" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-zinc-50 font-medium font-['Outfit'] text-lg">{fund.name}</h3>
                        <span className="px-2 py-0.5 text-xs font-mono text-emerald-400 bg-emerald-400/10 rounded-full">
                          {fund.reputation_score}/100
                        </span>
                        <span className="text-zinc-500 text-xs font-mono">{fund.portfolio_count} portfolio cos.</span>
                      </div>

                      <p className="text-zinc-400 text-sm font-['Manrope'] mb-2 line-clamp-2">{fund.investment_thesis}</p>

                      {/* Tags */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {fund.entry_stages?.map((s) => (
                          <span key={s} className="px-2 py-0.5 text-[10px] font-mono text-blue-400 bg-blue-400/10 rounded-full border border-blue-400/20 uppercase">
                            {s}
                          </span>
                        ))}
                        {fund.hq && (
                          <span className="flex items-center gap-1 text-zinc-500 text-xs">
                            <MapPin className="w-3 h-3" />
                            {fund.hq}
                          </span>
                        )}
                        {fund.aum && (
                          <span className="flex items-center gap-1 text-zinc-500 text-xs font-mono">
                            <DollarSign className="w-3 h-3" />
                            AUM: ${fund.aum}
                          </span>
                        )}
                      </div>
                    </div>

                    <ChevronRight className={cn(
                      "w-5 h-5 text-zinc-600 transition-transform flex-shrink-0",
                      expandedFund === fund.name && "rotate-90"
                    )} />
                  </div>
                </div>

                {/* Expanded Detail */}
                {expandedFund === fund.name && (
                  <div className="bg-zinc-900/60 border border-zinc-800 border-t-0 rounded-b-md p-5 space-y-5">
                    {loadingDetail ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
                      </div>
                    ) : fundDetail ? (
                      <>
                        {/* Check Sizes */}
                        <div>
                          <h4 className="text-zinc-400 text-xs font-mono uppercase tracking-wider mb-3">Check Sizes</h4>
                          <div className="grid grid-cols-4 gap-3">
                            {Object.entries(fundDetail.check_sizes || {}).map(([stage, range]) => (
                              <div key={stage} className="bg-zinc-800 rounded-md p-3">
                                <p className="text-zinc-500 text-[10px] font-mono uppercase mb-1">{stage.replace(/_/g, ' ')}</p>
                                <p className="text-zinc-50 font-mono text-sm">${range}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Sectors */}
                        <div>
                          <h4 className="text-zinc-400 text-xs font-mono uppercase tracking-wider mb-3">Focus Sectors</h4>
                          <div className="flex flex-wrap gap-2">
                            {fundDetail.sectors?.map((s) => (
                              <span key={s} className="px-3 py-1 text-xs font-mono text-zinc-300 bg-zinc-800 rounded-full">{s}</span>
                            ))}
                          </div>
                        </div>

                        {/* Partners */}
                        {fundDetail.notable_partners?.length > 0 && (
                          <div>
                            <h4 className="text-zinc-400 text-xs font-mono uppercase tracking-wider mb-3">Notable Partners</h4>
                            <div className="flex flex-wrap gap-2">
                              {fundDetail.notable_partners.map((p) => (
                                <span key={p} className="flex items-center gap-1 px-3 py-1 text-xs text-zinc-300 bg-zinc-800 rounded-full">
                                  <Users className="w-3 h-3 text-zinc-500" />
                                  {p}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Portfolio Preview */}
                        {fundDetail.portfolio?.length > 0 && (
                          <div>
                            <h4 className="text-zinc-400 text-xs font-mono uppercase tracking-wider mb-3">
                              Top Portfolio Companies ({fundDetail.portfolio_count})
                            </h4>
                            <div className="grid grid-cols-3 gap-2">
                              {fundDetail.portfolio.slice(0, 9).map((s) => (
                                <div
                                  key={s.startup_id}
                                  onClick={() => navigate(`/startups/${s.startup_id}`)}
                                  className="bg-zinc-800 rounded-md p-3 cursor-pointer hover:bg-zinc-700 transition-colors"
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="text-zinc-50 text-sm font-medium truncate">{s.name}</p>
                                    {s.investor_respect_score && (
                                      <span className="text-emerald-400 text-xs font-mono">{Math.round(s.investor_respect_score)}</span>
                                    )}
                                  </div>
                                  <p className="text-zinc-500 text-xs">{s.sector} | {s.stage}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Links */}
                        {fundDetail.website && (
                          <a href={fundDetail.website} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-400 text-sm hover:text-blue-300"
                          >
                            <Globe className="w-4 h-4" /> Visit Website <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default VCFundsPage;
